import * as amplitude from '@amplitude/analytics-browser';
import { Inject, Injectable, NgZone } from '@angular/core';
import { AuthDataService } from '@uzurv/http';
import { Subject, takeUntil } from 'rxjs';
import { APP_CONFIGURATION, AppConfiguration } from 'projects/uzurv/src/app/app-configuration';
import { AMPLITUDE_CONFIGURATION, AmplitudeConfiguration, isNullOrUndefined } from '@uzurv/core';

@Injectable({
  providedIn: 'root'
})
export class AmplitudeAnalyticsService {
  private destroyed$ = new Subject<void>();

  constructor(
    private authDataService: AuthDataService,
    @Inject(AMPLITUDE_CONFIGURATION) protected amplitudeConfiguration: AmplitudeConfiguration,
    @Inject(APP_CONFIGURATION) private appConfiguration: AppConfiguration,

    private ngZone: NgZone
  ) {}

  /**
   * Initializes Amplitude Analytics SDK if amplitudeSessionCaptureEnabled is true.
   * Must finish Analytics initialization before initializing Experiment SDK, otherwise exposure events fail.
   * @param amplitudeSessionCaptureEnabled
   */
  async init(amplitudeSessionCaptureEnabled: boolean) {
    if (amplitudeSessionCaptureEnabled) {
      await this.ngZone.runOutsideAngular(async () => {
        return amplitude
          .init(this.amplitudeConfiguration.amplitudeProjectKey, {
            appVersion: this.appConfiguration.version,
            autocapture: false,
            fetchRemoteConfig: true
          })
          .promise.then(() => {
            if (this.authDataService.isLoggedIn) {
              this.identifyAuthenticatedUser();
            }
            this.authDataService.loggedInUserDataStoredEvents.pipe(takeUntil(this.destroyed$)).subscribe(() => {
              if (this.authDataService.isLoggedIn) {
                this.identifyAuthenticatedUser();
              }
            });
            this.authDataService.logoutEvents.pipe(takeUntil(this.destroyed$)).subscribe(() => {
              amplitude.reset();
            });
          });
      });
    } else {
      return Promise.resolve();
    }
  }

  private identifyAuthenticatedUser() {
    if (isNullOrUndefined(this.authDataService.currentUser)) {
      amplitude.reset();
    } else {
      amplitude.setUserId(this.authDataService.currentUser.username);

      const identifyEvent = new amplitude.Identify();
      identifyEvent.set('name', `${this.authDataService.currentUser.firstName} ${this.authDataService.currentUser.lastName}`);
      identifyEvent.set('email', this.authDataService.currentUser.username);
      identifyEvent.set('claraPersonId', this.authDataService.currentUser.claraPersonId);
      identifyEvent.set('environment', this.appConfiguration.envName);
      amplitude.identify(identifyEvent);
    }
  }
}
